import { Portal, Text, Modal } from 'react-native-paper';
import { useGlobalSearchParams, useRouter } from 'expo-router';
import { ScrollView, View, StyleSheet } from 'react-native';
import BaseContainer from '@/components/BaseContainer';
import Form from '@/components/Form/Form';
import {
  Oeil,
  OeilSetInput,
  PatientSetInput,
  useGetParametresQuery,
  useInsertOeilMutation,
  useSubImplantsSubscription,
  useUpdatePatientMutation,
} from '@/data/data';
import ObjectHelper from '@/utils/ObjectHelper';
import FTextInput from '@/components/Form/TextInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import ScreenMargin from '@/components/ScreenMargin';
import { useUserContext } from '@/components/Auth/UserContext';
import { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useFormStyle from '@/components/Styles/useFormStyle';
import DatePickerInput from '@/components/Form/DatePickerInput';
import IconListInput from '@/components/Form/IconListInput';
import { FormSection } from '@/components/Form/FormSectionContext';
import CheckBoxInput from '@/components/Form/CheckBoxInput';
import parametersData from '@/data/parametersData';
import { usePatientContext } from '@/components/Contexts/PatientContext';
import { useToast } from 'react-native-toast-notifications';
import { useTranslation } from 'react-i18next';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Button from '@/components/Button';
import { useAsyncStorage } from '@react-native-async-storage/async-storage';
import { Color as ThemeColors } from '@/GlobalStyles';
import { defocusAbsisse } from '@/data/visionData';
import DefocusChart from '@/components/ScreenComponents/DefocusChart';
import Color from 'color';
import DropDownInput from '@/components/Form/DropDownInput';
import { getImplantsList } from '@/app/(app)/(stack)/patients/[detail]/(tab)/implants';

type SideType = 'ODG' | 'OG' | 'OD' | 'non';
type FormInputType = PatientSetInput & { oeilGauche?: OeilSetInput; oeilDroit?: OeilSetInput } & {
  aOperer: SideType;
  antecedantChirurgieCristallin: SideType;
  antecedantChirurgie: SideType;
};

const getResEyeValue = (currentValue: string, checkvalue: string) => {
  if (currentValue === 'ODG' || currentValue === checkvalue) {
    return '1';
  }
  return '-1';
};

const getGlobalEyeValue = (oeilDroit: Oeil, oeilGauche: Oeil, key: keyof Oeil) => {
  if (!oeilDroit && !oeilGauche) {
    return 'non';
  }
  if (oeilDroit[key] === '1' && oeilGauche[key] === '1') {
    return 'ODG';
  }
  if (oeilDroit[key] === '1') {
    return 'OD';
  }
  if (oeilGauche[key] === '1') {
    return 'OG';
  }
  return 'non';
};

const BiometriePage = () => {
  const { detail } = useGlobalSearchParams();

  const ref = useRef<typeof KeyboardAwareScrollView>();

  const { t, i18n } = useTranslation();
  const { data: disclaimer } = useGetParametresQuery({
    variables: { keyList: ['DISCLAIMER_' + i18n.language.toUpperCase()] },
  });

  const { patient } = usePatientContext();

  const { user, reloadUser } = useUserContext();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const [updatePatient] = useUpdatePatientMutation();
  const [insertOeil] = useInsertOeilMutation();
  const form = useForm<FormInputType>();

  const styles = useFormStyle();

  const sameValueValue = form.watch('sameValue');
  console.log('sameValueValue', sameValueValue);

  const { data: implants } = useSubImplantsSubscription();

  const toast = useToast();
  const hasViewedDisclaimer = useAsyncStorage('hasViewedDisclaimer');

  const [visible, setVisible] = useState(false);

  const readItemFromStorage = async () => {
    const item = parseInt((await hasViewedDisclaimer.getItem()) || '') || 0;
    //on regarde si ca fait plus de 24h
    setVisible(new Date().getTime() - item > 86400000);
  };

  useEffect(() => {
    readItemFromStorage();
  }, []);

  const showModal = () => setVisible(true);
  const hideModal = () => {
    setVisible(false);
    hasViewedDisclaimer.setItem(new Date().getTime().toString());
  };

  const defaultValues: (FormInputType & typeof patient) | undefined | null =
    ObjectHelper.createDateValues<typeof patient>(patient, 'birthday');
  if (defaultValues) {
    const oeils = Object.values(defaultValues.oeils || {});
    defaultValues.oeilGauche = oeils.find((oeil) => oeil.side === 'left');
    defaultValues.oeilDroit = oeils.find((oeil) => oeil.side === 'right');
    defaultValues.aOperer = getGlobalEyeValue(
      defaultValues.oeilDroit,
      defaultValues.oeilGauche,
      'aOperer'
    );
    defaultValues.antecedantChirurgie = getGlobalEyeValue(
      defaultValues.oeilDroit,
      defaultValues.oeilGauche,
      'antecedantChirurgie'
    );
    defaultValues.antecedantChirurgieCristallin = getGlobalEyeValue(
      defaultValues.oeilDroit,
      defaultValues.oeilGauche,
      'antecedantChirurgieCristallin'
    );
  }

  const defocusOeilDroitChartOrdonnees = form.watch('oeilDroit.courbeDefocus') || [];
  const defocusOeilDroitChartData = defocusAbsisse.map((value, k) => [
    value,
    defocusOeilDroitChartOrdonnees[k] !== undefined ? defocusOeilDroitChartOrdonnees[k] : null,
  ]);

  const defocusOeilGaucheChartOrdonnees = form.watch('oeilGauche.courbeDefocus') || [];
  const defocusOeilGaucheChartData = defocusAbsisse.map((value, k) => [
    value,
    defocusOeilGaucheChartOrdonnees[k] !== undefined ? defocusOeilGaucheChartOrdonnees[k] : null,
  ]);

  return (
    <BaseContainer withScrollView={'keyboardAware'} ref={ref}>
      <ScreenMargin>
        <Portal>
          <Modal
            visible={visible}
            onDismiss={hideModal}
            contentContainerStyle={style.containerStyle}
          >
            <Text variant={'titleLarge'} style={{ marginBottom: 15 }}>
              {t('Bienvenue sur IOL Match !')}
            </Text>
            <ScrollView style={{ marginBottom: 55 }}>
              <Text>{disclaimer?.parametres?.[0]?.paramValue}</Text>
            </ScrollView>
            <Button
              onPress={hideModal}
              style={{ alignSelf: 'flex-end', position: 'absolute', bottom: 20 }}
            >
              {t('Fermer')}
            </Button>
          </Modal>
        </Portal>
        <Form<FormInputType> {...form} defaultValues={defaultValues}>
          <View style={styles?.bodyForm}>
            <FormSection sectionOrder={1}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('Données')}</Text>

                <IconListInput
                  name={'sexe'}
                  label={'Sexe'}
                  withHelpButton={true}
                  data={[
                    {
                      value: 1,
                      label: t('Masculin'),
                      icon: 'gender-male',
                    },
                    {
                      value: 2,
                      label: t('Féminin'),
                      icon: 'gender-female',
                    },
                  ]}
                  containerStyle={styles.input2Col}
                />
                <View style={styles.input2Col}></View>
                <FTextInput
                  name={'size'}
                  label={'Taille en cm'}
                  withHelpButton={true}
                  helpFieldName="taille"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                />
                <DatePickerInput
                  name={'birthday'}
                  label={t('Date de naissance')}
                  helpFieldName="dateNaissance"
                  withHelpButton={true}
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  validators={[AvailableValidationRules.required]}
                />
                <IconListInput
                  name={'photicEffect'}
                  label={'Le patient accepte les effets photiques ?'}
                  withHelpButton={true}
                  data={parametersData.ouiNonInput}
                  containerStyle={styles.input2Col}
                />
                <IconListInput
                  name={'glasses'}
                  label={'Le patient souhaite être indépendant aux lunettes ?'}
                  withHelpButton={true}
                  data={parametersData.ouiNonInput}
                  containerStyle={styles.input2Col}
                />
              </View>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('Informations générales')}</Text>
                <View style={styles.rowForm}>
                  <View style={[styles.input2Col, { flexDirection: 'column' }]}>
                    <IconListInput
                      name={'aOperer'}
                      label={'Oeil à opérer'}
                      data={[
                        {
                          value: 'ODG',
                          label: t('ODG'),
                        },
                        {
                          value: 'OD',
                          label: t('OD'),
                        },
                        {
                          value: 'OG',
                          label: t('OG'),
                        },
                      ]}
                      onChangeValue={(value) => {
                        if (value === 'ODG') {
                          form.setValue('etatControlateral', 0);
                        }
                      }}
                      withBottomBorder
                    />
                    <CheckBoxInput
                      name={'sameValue'}
                      label={'Les deux yeux ont les mêmes caractéristiques biométriques'}
                      onChangeValue={(value) => {
                        if (value) {
                          form.setValue('etatControlateral', 0);
                        }
                      }}
                    />
                    <CheckBoxInput
                      name={'etatControlateral'}
                      label={'Je souhaite tout de même renseigner l’état de l’oeil controlatéral'}
                      withHelpButton={true}
                      helpFieldName="etatControlateral"
                      disabled={form.watch('aOperer') === 'ODG' || form.watch('sameValue') === 1}
                      containerStyle={{ marginBottom: 20 }}
                    />
                    <IconListInput
                      name={'antecedantChirurgie'}
                      label={'Antécédents de chirurgie réfractive cornéenne'}
                      data={[
                        {
                          value: 'non',
                          label: t('Non'),
                        },
                        {
                          value: 'ODG',
                          label: t('ODG'),
                        },
                        form.watch('sameValue')
                          ? null
                          : {
                              value: 'OD',
                              label: t('OD'),
                            },
                        form.watch('sameValue')
                          ? null
                          : {
                              value: 'OG',
                              label: t('OG'),
                            },
                      ].filter((item) => item !== null)}
                      withMargin
                    />
                    <IconListInput
                      name={'antecedantChirurgieCristallin'}
                      label={'Antécédents de chirurgie du cristallin'}
                      data={[
                        {
                          value: 'non',
                          label: t('Non'),
                        },
                        {
                          value: 'ODG',
                          label: t('ODG'),
                        },
                        form.watch('sameValue')
                          ? null
                          : {
                              value: 'OD',
                              label: t('OD'),
                            },
                        form.watch('sameValue')
                          ? null
                          : {
                              value: 'OG',
                              label: t('OG'),
                            },
                      ].filter((item) => item !== null)}
                    />
                  </View>
                </View>
                <View style={[styles.rowForm]}>
                  {form.watch('antecedantChirurgieCristallin') === 'ODG' ||
                  form.watch('antecedantChirurgieCristallin') === 'OD' ? (
                    <View style={[styles.colGroupForm, styles.input2Col]}>
                      <View
                        style={[
                          styles.colGroupForm,
                          styles.oeilContainer,
                          { backgroundColor: Color(ThemeColors.MainBleu).lighten(1.5).hex() },
                        ]}
                      >
                        <Text style={styles?.titleSubGroupCol}>{t('Oeil droit')}</Text>
                        <IconListInput
                          name={'oeilDroit.typeImplantActuel'}
                          label={'Type d’implant actuel'}
                          data={[
                            {
                              value: 'Monofocal',
                              label: t('Monofocal'),
                            },
                            {
                              value: 'EDOF',
                              label: t('EDOF'),
                            },
                            {
                              value: 'MIOL',
                              label: t('MIOL'),
                            },
                            {
                              value: 'n/a',
                              label: 'N/A',
                              activeColor: ThemeColors.LevelNA,
                              inactiveColor: ThemeColors.LevelNA,
                            },
                          ]}
                        />
                        {form.watch('aOperer') === 'ODG' || form.watch('aOperer') === 'OD' ? (
                          <CheckBoxInput
                            name={'oeilDroit.ajoutAddon'}
                            label={'Je souhaite mettre un AddOn'}
                          />
                        ) : null}
                        <IconListInput
                          name={'oeilDroit.genererCourbeTypeImplant'}
                          label={'Courbe de défocus'}
                          data={[
                            form.watch('oeilDroit.typeImplantActuel') !== 'n/a'
                              ? {
                                  value: 'type',
                                  label:
                                    t('Courbe "') +
                                    t(form.watch('oeilDroit.typeImplantActuel')) +
                                    t('" par défaut'),
                                }
                              : null,
                            {
                              value: 'patient',
                              label: t('Courbe du patient'),
                            },
                            form.watch('oeilDroit.typeImplantActuel') !== 'n/a'
                              ? {
                                  value: 'implant',
                                  label: "Courbe de l'implant",
                                }
                              : null,
                            {
                              value: 'n/a',
                              label: 'N/A',
                              activeColor: ThemeColors.LevelNA,
                              inactiveColor: ThemeColors.LevelNA,
                            },
                          ].filter((item) => item !== null)}
                        />
                        {form.watch('oeilDroit.genererCourbeTypeImplant') === 'implant' ? (
                          <DropDownInput
                            name={'oeilDroit.implantCourantId'}
                            label={'Implant'}
                            placeholder={t('Sélectionner un implant')}
                            searchPlaceholder={t('Chercher un implant')}
                            searchable={true}
                            categorySelectable={false}
                            items={
                              getImplantsList(implants, undefined, undefined, undefined, t) || []
                            }
                            maxHeight={250}
                            zIndex={7000}
                            zIndexInverse={6000}
                            listParentLabelStyle={{ fontWeight: 'bold' }}
                            containerStyle={{ marginTop: 20 }}
                          />
                        ) : null}
                        {form.watch('oeilDroit.genererCourbeTypeImplant') === 'patient' ? (
                          <>
                            <View>
                              {defocusAbsisse.map((value, k) => (
                                <FTextInput
                                  name={`oeilDroit.courbeDefocus.${k}`}
                                  label={`${value} δ`}
                                  key={k}
                                  keyboardType={'decimal-pad'}
                                  withHelpButton={k === 0}
                                  helpFieldName="courbeDefocus"
                                  withMargin={false}
                                />
                              ))}
                            </View>
                            <DefocusChart data={defocusOeilDroitChartData} />
                          </>
                        ) : null}
                      </View>
                    </View>
                  ) : null}
                  {form.watch('antecedantChirurgieCristallin') === 'ODG' ||
                  form.watch('antecedantChirurgieCristallin') === 'OG' ? (
                    <View style={[styles.colGroupForm, styles.input2Col]}>
                      <View
                        style={[
                          styles.colGroupForm,
                          styles.oeilContainer,
                          { backgroundColor: Color(ThemeColors.sECJAUNE).lighten(0.7).hex() },
                        ]}
                      >
                        <Text style={styles?.titleSubGroupCol}>{t('Oeil gauche')}</Text>
                        <IconListInput
                          name={'oeilGauche.typeImplantActuel'}
                          label={'Type d’implant actuel'}
                          data={[
                            {
                              value: 'Monofocal',
                              label: t('Monofocal'),
                            },
                            {
                              value: 'EDOF',
                              label: t('EDOF'),
                            },
                            {
                              value: 'MIOL',
                              label: t('MIOL'),
                            },
                            {
                              value: 'n/a',
                              label: 'N/A',
                              activeColor: ThemeColors.LevelNA,
                              inactiveColor: ThemeColors.LevelNA,
                            },
                          ]}
                        />
                        {form.watch('aOperer') === 'ODG' || form.watch('aOperer') === 'OG' ? (
                          <CheckBoxInput
                            name={'oeilGauche.ajoutAddon'}
                            label={'Je souhaite mettre un AddOn'}
                          />
                        ) : null}
                        <IconListInput
                          name={'oeilGauche.genererCourbeTypeImplant'}
                          label={'Courbe de défocus'}
                          data={[
                            form.watch('oeilGauche.typeImplantActuel') !== 'n/a'
                              ? {
                                  value: 'type',
                                  label:
                                    t('Courbe "') +
                                    t(form.watch('oeilGauche.typeImplantActuel')) +
                                    t('" par défaut'),
                                }
                              : null,
                            {
                              value: 'patient',
                              label: t('Courbe du patient'),
                            },
                            form.watch('oeilGauche.typeImplantActuel') !== 'n/a'
                              ? {
                                  value: 'implant',
                                  label: "Courbe de l'implant",
                                }
                              : null,
                            {
                              value: 'n/a',
                              label: 'N/A',
                              activeColor: ThemeColors.LevelNA,
                              inactiveColor: ThemeColors.LevelNA,
                            },
                          ].filter((item) => item !== null)}
                        />
                        {form.watch('oeilGauche.genererCourbeTypeImplant') === 'implant' ? (
                          <DropDownInput
                            name={'oeilGauche.implantCourantId'}
                            label={'Implant'}
                            placeholder={t('Sélectionner un implant')}
                            searchPlaceholder={t('Chercher un implant')}
                            searchable={true}
                            categorySelectable={false}
                            items={
                              getImplantsList(implants, undefined, undefined, undefined, t) || []
                            }
                            maxHeight={250}
                            zIndex={7000}
                            zIndexInverse={6000}
                            listParentLabelStyle={{ fontWeight: 'bold' }}
                            containerStyle={{ marginTop: 20 }}
                          />
                        ) : null}
                        {form.watch('oeilGauche.genererCourbeTypeImplant') === 'patient' ? (
                          <>
                            <View>
                              {defocusAbsisse.map((value, k) => (
                                <FTextInput
                                  name={`oeilGauche.courbeDefocus.${k}`}
                                  label={`${value} δ`}
                                  key={k}
                                  keyboardType={'decimal-pad'}
                                  withHelpButton={k === 0}
                                  helpFieldName="courbeDefocus"
                                  withMargin={false}
                                />
                              ))}
                            </View>
                            <DefocusChart data={defocusOeilGaucheChartData} />
                          </>
                        ) : null}
                      </View>
                    </View>
                  ) : null}
                </View>
              </View>
            </FormSection>
            {sameValueValue ||
            form.watch('aOperer') === 'ODG' ||
            form.watch('aOperer') === 'OD' ||
            form.watch('etatControlateral') ? (
              <FormSection sectionOrder={2}>
                <View style={styles?.groupForm}>
                  <Text style={styles?.titleGroupForm}>
                    {sameValueValue ? t('Oeil droit et oeil gauche') : t('Oeil droit')}
                  </Text>
                  {form.watch('antecedantChirurgie') === 'ODG' ||
                  form.watch('antecedantChirurgie') === 'OD' ? (
                    <>
                      <Text style={styles?.titleSubGroupForm}>{t('Informations générales')}</Text>
                      <View style={[styles.rowForm, { zIndex: 1 }]}>
                        <IconListInput
                          name={'oeilDroit.bump'}
                          label={'Bump'}
                          withHelpButton={true}
                          helpFieldName="bump"
                          data={parametersData.bump}
                          containerStyle={styles.input2Col}
                        />
                      </View>
                    </>
                  ) : null}
                  <Text style={styles?.titleSubGroupForm}>{t("État de l'oeil")}</Text>
                  <IconListInput
                    name={'oeilDroit.larmes'}
                    label={'Larmes'}
                    withHelpButton={true}
                    helpFieldName="larmes"
                    data={parametersData.larmes}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.aberrationOrdreEleve'}
                    label={"Cornée - Aberration d'ordre élevé"}
                    withHelpButton={true}
                    helpFieldName="aberrationOrdreEleve"
                    data={parametersData.aberrationOrdreEleve}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.aberrationSpherique'}
                    label={'Cornée - Aberration sphérique'}
                    withHelpButton={true}
                    helpFieldName="aberrationSpherique"
                    data={parametersData.aberrationSpherique}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.endothelium'}
                    label={'Cornée - Endothelium'}
                    withHelpButton={true}
                    helpFieldName="endothelium"
                    data={parametersData.endothelium}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.diametrePupillaire'}
                    label={'Diamètre pupillaire'}
                    withHelpButton={true}
                    helpFieldName="diametrePupillaire"
                    data={parametersData.diametrePupillaire}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.retineNerfOptique'}
                    label={'Rétine et/ou nerf optique'}
                    withHelpButton={true}
                    helpFieldName="retineNerfOptique"
                    data={parametersData.retineNerfOptique}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilDroit.longueurAxiale'}
                    label={'Longueur axiale'}
                    withHelpButton={true}
                    helpFieldName="longueurAxiale"
                    data={parametersData.longueurAxiale}
                    containerStyle={styles.input2Col}
                  />
                </View>
              </FormSection>
            ) : null}
            {!sameValueValue &&
            (form.watch('aOperer') === 'ODG' ||
              form.watch('aOperer') === 'OG' ||
              form.watch('etatControlateral')) ? (
              <FormSection
                sectionOrder={
                  !(
                    sameValueValue ||
                    form.watch('aOperer') === 'ODG' ||
                    form.watch('aOperer') === 'OD' ||
                    form.watch('etatControlateral')
                  )
                    ? 2
                    : 3
                }
              >
                <View style={styles?.groupForm}>
                  <Text style={styles?.titleGroupForm}>{t('Oeil gauche')}</Text>
                  {form.watch('antecedantChirurgie') === 'ODG' ||
                  form.watch('antecedantChirurgie') === 'OG' ? (
                    <>
                      <Text style={styles?.titleSubGroupForm}>{t('Informations générales')}</Text>
                      <View style={[styles.rowForm, { zIndex: 1 }]}>
                        <IconListInput
                          name={'oeilGauche.bump'}
                          label={'Bump'}
                          withHelpButton={true}
                          helpFieldName="bump"
                          data={parametersData.bump}
                          containerStyle={styles.input2Col}
                        />
                      </View>
                    </>
                  ) : null}
                  <Text style={styles?.titleSubGroupForm}>{t("État de l'oeil")}</Text>
                  <IconListInput
                    name={'oeilGauche.larmes'}
                    label={'Larmes'}
                    withHelpButton={true}
                    helpFieldName="larmes"
                    data={parametersData.larmes}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.aberrationOrdreEleve'}
                    label={"Cornée - Aberration d'ordre élevé"}
                    withHelpButton={true}
                    helpFieldName="aberrationOrdreEleve"
                    data={parametersData.aberrationOrdreEleve}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.aberrationSpherique'}
                    label={'Cornée - Aberration sphérique'}
                    withHelpButton={true}
                    helpFieldName="aberrationSpherique"
                    data={parametersData.aberrationSpherique}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.endothelium'}
                    label={'Cornée - Endothelium'}
                    withHelpButton={true}
                    helpFieldName="endothelium"
                    data={parametersData.endothelium}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.diametrePupillaire'}
                    label={'Diamètre pupillaire'}
                    withHelpButton={true}
                    helpFieldName="diametrePupillaire"
                    data={parametersData.diametrePupillaire}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.retineNerfOptique'}
                    label={'Rétine et/ou nerf optique'}
                    withHelpButton={true}
                    helpFieldName="retineNerfOptique"
                    data={parametersData.retineNerfOptique}
                    withBottomBorder
                    containerStyle={[styles.input2Col]}
                  />
                  <IconListInput
                    name={'oeilGauche.longueurAxiale'}
                    label={'Longueur axiale'}
                    withHelpButton={true}
                    helpFieldName="longueurAxiale"
                    data={parametersData.longueurAxiale}
                    containerStyle={styles.input2Col}
                  />
                </View>
              </FormSection>
            ) : null}
          </View>
          <View style={styles?.footerForm}>
            <FormSectionFooter
              saveLoading={loading}
              saveDisabled={loading}
              beforeChange={() => {
                if (ref.current) {
                  (ref.current as unknown as KeyboardAwareScrollView).scrollToPosition(0, 0);
                }
              }}
              onPress={form.handleSubmit(async (data) => {
                setLoading(true);
                if (!user) {
                  await reloadUser();
                }
                if (user) {
                  await updatePatient({
                    variables: {
                      data: {
                        birthday: data.birthday,
                        glasses: data.glasses,
                        sameValue: data.sameValue,
                        etatControlateral: data.etatControlateral,
                        photicEffect: data.photicEffect,
                        sexe: data.sexe,
                        size: data.size,
                      },
                      id: detail,
                    },
                    fetchPolicy: 'no-cache',
                  });
                  let aOperer = getResEyeValue(data.aOperer, 'OD');
                  let antecedantChirurgieCristallin = getResEyeValue(
                    data.antecedantChirurgieCristallin,
                    'OD'
                  );
                  await insertOeil({
                    fetchPolicy: 'no-cache',
                    variables: {
                      object: {
                        ...data.oeilDroit,
                        aOperer: aOperer,
                        antecedantChirurgie: getResEyeValue(data.antecedantChirurgie, 'OD'),
                        antecedantChirurgieCristallin: antecedantChirurgieCristallin,
                        typeImplantActuel: antecedantChirurgieCristallin
                          ? data.oeilDroit?.typeImplantActuel
                          : null,
                        genererCourbeTypeImplant: antecedantChirurgieCristallin
                          ? data.oeilDroit?.genererCourbeTypeImplant
                          : null,
                        ajoutAddon:
                          antecedantChirurgieCristallin && aOperer
                            ? data.oeilDroit?.ajoutAddon
                            : null,
                        courbeDefocus:
                          antecedantChirurgieCristallin &&
                          data.oeilDroit?.genererCourbeTypeImplant === 'patient'
                            ? data.oeilDroit?.courbeDefocus
                            : null,
                        implantCourantId:
                          antecedantChirurgieCristallin &&
                          data.oeilDroit?.genererCourbeTypeImplant === 'implant'
                            ? data.oeilDroit?.implantCourantId
                            : null,
                        side: 'right',
                        patientId: detail,
                      },
                    },
                  });
                  aOperer = getResEyeValue(data.aOperer, 'OG');
                  antecedantChirurgieCristallin = getResEyeValue(
                    data.antecedantChirurgieCristallin,
                    'OG'
                  );
                  await insertOeil({
                    fetchPolicy: 'no-cache',
                    variables: {
                      object: {
                        ...(data.sameValue ? data.oeilDroit : data.oeilGauche),
                        aOperer: aOperer,
                        antecedantChirurgie: getResEyeValue(data.antecedantChirurgie, 'OG'),
                        antecedantChirurgieCristallin: antecedantChirurgieCristallin,
                        typeImplantActuel: antecedantChirurgieCristallin
                          ? data.oeilGauche?.typeImplantActuel
                          : null,
                        genererCourbeTypeImplant: antecedantChirurgieCristallin
                          ? data.oeilGauche?.genererCourbeTypeImplant
                          : null,
                        ajoutAddon:
                          antecedantChirurgieCristallin && aOperer
                            ? data.oeilGauche?.ajoutAddon
                            : null,
                        courbeDefocus:
                          antecedantChirurgieCristallin &&
                          data.oeilGauche?.genererCourbeTypeImplant === 'patient'
                            ? data.oeilGauche?.courbeDefocus
                            : null,
                        implantCourantId:
                          antecedantChirurgieCristallin &&
                          data.oeilGauche?.genererCourbeTypeImplant === 'implant'
                            ? data.oeilGauche?.implantCourantId
                            : null,
                        side: 'left',
                        patientId: detail,
                      },
                    },
                  });
                  toast.show(t('Biométrie enregistrée'), {
                    type: 'success',
                    placement: 'center',
                    duration: 1000,
                    animationType: 'slide-in',
                    onPress: (id) => {
                      toast.hide(id);
                    },
                  });
                  router.push(`/patients/${detail}/habitudes`);
                }
                setLoading(false);
              })}
            />
          </View>
        </Form>
      </ScreenMargin>
    </BaseContainer>
  );
};

export default BiometriePage;

const style = StyleSheet.create({
  containerStyle: {
    backgroundColor: 'white',
    padding: 20,
    width: '80%',
    height: '80%',
    alignSelf: 'center',
    justifyContent: 'flex-start',
  },
});
